import React from "react";
import { createPortal } from "react-dom";
import { useAndroidBack } from "../hooks/useAndroidBack";
import { T } from "../i18n/T";
import { t } from "../i18n";
import "./FanPointsPopup.css";

// A tap on the points on My Profile (founder's mock, 2026-11-02): short and fun.
// Levels come from points EARNED, one per 1,000 (server lib/fanPoints.js), and
// the ring tops out at the rainbow on level 5. A Fan Boost is what points buy, at
// the server's price (stats.pointsBoostCost). The card's outline and glow wear
// the fan's level, like the ring and the points pill. With Reduce Motion the star fades
// in and its rays twinkle; nothing moves.

const STAR = "M12 1.8l3.1 6.5 7.1.9-5.2 4.9 1.4 7.1L12 17.3l-6.4 3.6 1.4-7.1L1.8 9.2l7.1-.9z";
const bold = { 0: (c: React.ReactNode) => <b>{c}</b>, 1: (c: React.ReactNode) => <b>{c}</b> };

type Props = { points: number; level: number; boostCost: number; onClose: () => void };

const FanPointsPopup: React.FC<Props> = ({ points, level, boostCost, onClose }) => {
  useAndroidBack(onClose);
  const next = level + 1;
  return createPortal(
    <div className="fpp-overlay" onClick={onClose} role="presentation">
      <div className={`fpp${level > 0 ? ` fph-points--${Math.min(level, 5)}` : ""}`} role="dialog" aria-modal="true" aria-labelledby="fpp-title" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="fpp-x" onClick={onClose} aria-label={t("reviewSheet.close")}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <svg className="fpp-star" viewBox="0 0 120 92" aria-hidden="true">
          <defs>
            <linearGradient id="fppGold" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#ffe27a" />
              <stop offset="1" stopColor="#f5a623" />
            </linearGradient>
          </defs>
          <g className="fpp-rays" stroke="#ffd34d" strokeWidth="3.2" strokeLinecap="round">
            <path d="M31 19l-10-7" />
            <path d="M25 44H12" />
            <path d="M89 19l10-7" />
            <path d="M95 44h13" />
          </g>
          <g className="fpp-star-shape">
            <path transform="translate(26 12) scale(2.84)" d={STAR} fill="url(#fppGold)" stroke="#ffe27a" strokeWidth=".5" strokeLinejoin="round" />
          </g>
        </svg>
        <h2 id="fpp-title" className="fpp-title">{t("fanProfilePoints.title")}</h2>
        <div className="fpp-points">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d={STAR} fill="url(#fppGold)" /></svg>
          {t("fanProfileEditorSheet.pts", { points: points.toLocaleString() }).trim()}
        </div>
        {level > 0 && <div className={`fpp-level fph-points--${Math.min(level, 5)}`}>{t("fanProfilePoints.level", { level })}</div>}
        <p className="fpp-text">
          <span><T k="fanProfilePoints.earn" tags={bold} /></span>{" "}
          <span><T k="fanProfilePoints.boost" vars={{ points: boostCost.toLocaleString() }} tags={bold} /></span>{" "}
          <span>
            {level >= 5 ? (
              <T k="fanProfilePoints.top" tags={bold} />
            ) : (
              <T k="fanProfilePoints.next" vars={{ points: (next * 1000).toLocaleString(), level: next }} tags={bold} />
            )}
          </span>
        </p>
        <button type="button" className="fpp-go" onClick={onClose}>{t("reviewSheet.gotIt")}</button>
      </div>
    </div>,
    document.body
  );
};

export default FanPointsPopup;
